/**
 * SignWaiver — public page where an additional adult guest signs their own
 * charter waiver before the trip, reached via the tokenized link in the
 * booking confirmation email (/sign/:token). No login.
 *
 * Loads the booking context (GET /api/waivers/booking/:token) + the approved
 * waiver (GET /api/waivers/current), renders the document with required
 * acknowledgments + a typed e-signature, and POSTs to /api/waivers/sign.
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

import api from '../lib/api';
import WaiverDocument, {
  adultAge,
  renderSignaturePng,
} from '../components/WaiverDocument';

const fieldStyle = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  fontSize: '1rem',
  border: '1px solid rgba(0,0,0,0.18)',
  borderRadius: '8px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.78rem',
  fontWeight: 700,
  color: 'rgba(0,0,0,0.6)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '0.35rem',
};

function Field({ id, label, type = 'text', value, onChange, error, required }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label htmlFor={id} style={labelStyle}>
        {label}
        {required ? ' *' : ''}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        data-testid={id}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...fieldStyle, borderColor: error ? '#9F1239' : 'rgba(0,0,0,0.18)' }}
      />
      {error ? (
        <p style={{ margin: '0.3rem 0 0', color: '#9F1239', fontSize: '0.8rem' }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

function formatYmd(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function SignWaiver() {
  const { token } = useParams();

  const [ctx, setCtx] = useState(null); // booking summary
  const [waiver, setWaiver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [acks, setAcks] = useState({});
  const [photoChoice, setPhotoChoice] = useState(null);
  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [email, setEmail] = useState('');

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/api/waivers/booking/${token}`),
      api.get('/api/waivers/current'),
    ])
      .then(([bRes, wRes]) => {
        if (cancelled) return;
        setCtx(bRes.data?.booking || null);
        setWaiver(wRes.data || null);
        setLoadError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.error ||
            err?.message ||
            'This link is invalid or expired.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const requiredAckIds = waiver?.required_ack_ids || [];

  const validate = () => {
    const next = {};
    if (requiredAckIds.some((id) => acks[id] !== true)) {
      next.acks = 'Please check every required box.';
    }
    if (photoChoice !== true && photoChoice !== false) {
      next.photo = 'Please choose a photo/media option (Section 13).';
    }
    if (!fullName.trim()) next.fullName = 'Type your full legal name to sign.';
    const age = adultAge(dob);
    if (age == null) next.dob = 'Enter a valid date of birth.';
    else if (age < 18) {
      next.dob = 'Guests under 18 are signed for by the guardian who booked.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      next.email = 'Enter a valid email address.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post('/api/waivers/sign', {
        token,
        full_legal_name: fullName.trim(),
        dob,
        email: email.trim(),
        acknowledgments: acks,
        photo_release: photoChoice,
        signature_image: renderSignaturePng(fullName.trim()),
        accepted_version: waiver.version,
      });
      setDone(true);
    } catch (err) {
      setSubmitError(
        err?.response?.data?.error ||
          err?.message ||
          'Something went wrong. Please try again, or call Captain Matt at 904-243-9044.',
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign your charter waiver — Nowhere Charters</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main style={{ maxWidth: '700px', margin: '0 auto', padding: '2rem 1.5rem 4rem' }}>
        {loading ? (
          <p>Loading…</p>
        ) : loadError ? (
          <section style={{ textAlign: 'center' }}>
            <h1 style={{ color: '#0D2351' }}>Waiver link unavailable</h1>
            <p style={{ color: 'rgba(0,0,0,0.7)' }}>{loadError}</p>
            <p style={{ color: 'rgba(0,0,0,0.7)' }}>
              Call or text Captain Matt at{' '}
              <a href="tel:+19042439044" style={{ color: '#0D2351', fontWeight: 700 }}>
                904-243-9044
              </a>
              .
            </p>
          </section>
        ) : done ? (
          <section style={{ textAlign: 'center', maxWidth: '520px', margin: '0 auto' }}>
            <div
              aria-hidden="true"
              style={{
                width: '64px',
                height: '64px',
                margin: '1rem auto 1.25rem',
                borderRadius: '50%',
                background: '#0D2351',
                color: '#F6C300',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                fontWeight: 700,
              }}
            >
              &#10003;
            </div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#0D2351' }}>
              You&rsquo;re signed — thank you!
            </h1>
            <p style={{ marginTop: '0.5rem', color: 'rgba(0,0,0,0.7)' }}>
              Your waiver is on file for this charter. See you on the water.
            </p>
          </section>
        ) : (
          <>
            <h1 style={{ marginTop: 0, fontSize: '1.6rem', color: '#0D2351' }}>
              Sign your charter waiver
            </h1>
            <p style={{ marginTop: '0.25rem', marginBottom: '1.25rem', color: 'rgba(0,0,0,0.65)' }}>
              {ctx?.trip_name ? `${ctx.trip_name} · ` : ''}
              {ctx?.charter_date ? formatYmd(ctx.charter_date) : ''}
              {ctx?.lead_first_name ? ` · booked by ${ctx.lead_first_name}` : ''}
              {ctx?.booking_number ? ` · #${ctx.booking_number}` : ''}
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <WaiverDocument
                waiver={waiver}
                acks={acks}
                setAcks={setAcks}
                photoChoice={photoChoice}
                setPhotoChoice={setPhotoChoice}
              />

              {errors.acks ? (
                <p style={{ margin: '0 0 0.75rem', color: '#9F1239', fontSize: '0.85rem' }}>
                  {errors.acks}
                </p>
              ) : null}
              {errors.photo ? (
                <p style={{ margin: '0 0 0.75rem', color: '#9F1239', fontSize: '0.85rem' }}>
                  {errors.photo}
                </p>
              ) : null}

              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem', color: '#0D2351' }}>
                Sign electronically
              </h3>
              <Field
                id="sign-fullname"
                label="Full legal name (your electronic signature)"
                value={fullName}
                onChange={setFullName}
                error={errors.fullName}
                required
              />
              <Field
                id="sign-dob"
                label="Date of birth"
                type="date"
                value={dob}
                onChange={setDob}
                error={errors.dob}
                required
              />
              <Field
                id="sign-email"
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                error={errors.email}
                required
              />

              {fullName.trim() ? (
                <p
                  aria-hidden="true"
                  style={{
                    margin: '0 0 1rem',
                    fontFamily: '"Brush Script MT", "Segoe Script", cursive',
                    fontStyle: 'italic',
                    fontSize: '1.6rem',
                    color: '#0D2351',
                  }}
                >
                  {fullName.trim()}
                </p>
              ) : null}

              {submitError ? (
                <p
                  role="alert"
                  data-testid="sign-error"
                  style={{
                    padding: '0.75rem 1rem',
                    background: '#FEF2F2',
                    color: '#9F1239',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    marginBottom: '1rem',
                  }}
                >
                  {submitError}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                data-testid="sign-submit"
                style={{
                  width: '100%',
                  padding: '0.85rem 1.1rem',
                  background: submitting ? 'rgba(13,35,81,0.5)' : '#0D2351',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  cursor: submitting ? 'wait' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {submitting ? 'Submitting…' : 'Sign waiver'}
              </button>
            </form>
          </>
        )}
      </main>
    </>
  );
}
